import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ShoppingCart, Plus, Send, PackageCheck, X, Trash2, Loader2, FileText } from 'lucide-react';
import { apiErrorMessage } from './utils/apiError';
import { formatMoney } from './utils/money';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v != null));
// Money/quantities arrive from the API as exact decimal STRINGS (backend
// exact_decimal.py) and are sent back as strings. Number() is used for DISPLAY
// formatting only — never for arithmetic on money.
const fmt = (v, dp = 2) => (Number(v) || 0).toFixed(dp);
const decStr = (v) => String(v ?? '').trim();

const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => {
        if (cancelled || !ctx) return;
        const id = ctx?.organizations?.[0]?.id;
        if (id) { setOrgId(id); try { localStorage.setItem('current_organization_id', id); } catch {} }
      }).catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
    ...(localStorage.getItem('current_location_id') ? { 'X-Location-Id': localStorage.getItem('current_location_id') } : {}),
  }), [token, orgId]);
  return { headers, orgId };
};

const statusBadge = (s) => ({
  draft: 'bg-surface-sunken text-ink-muted',
  sent: 'bg-primary-50 text-primary-700',
  partially_received: 'bg-warn/10 text-warn',
  received: 'bg-ok/10 text-ok',
  cancelled: 'bg-danger/10 text-danger',
}[s] || 'bg-surface-sunken text-ink-muted');

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-surface-border" data-testid={`proc-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center"><Icon className="w-5 h-5 text-primary-500" /></div>
      <div><div className="text-2xl font-display text-ink tabular-nums">{value}</div><div className="text-xs text-ink-muted">{label}</div></div>
    </div>
  </div>
);

const Procurement = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers, orgId } = useOrgScopedHeaders(token);

  const [pos, setPos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [receiveError, setReceiveError] = useState(null);
  const [form, setForm] = useState({ supplier_name: '', notes: '', lines: [{ description: '', qty: '', unit: '', unit_cost: '' }] });
  const [detail, setDetail] = useState(null);
  const [receiveState, setReceiveState] = useState(null); // {po, lines:[{received_qty, actual_unit_cost}]}
  const [suggestions, setSuggestions] = useState([]);

  const load = useCallback(async () => {
    if (!headers['X-Organization-Id']) { setLoading(false); return; }
    setLoading(true);
    try {
      const [pRes, sRes, gRes] = await Promise.all([
        fetch(`${API_URL}/api/procurement/purchase-orders`, { headers }),
        fetch(`${API_URL}/api/procurement/stats`, { headers }),
        fetch(`${API_URL}/api/procurement/suggestions`, { headers }),
      ]);
      setPos(pRes.ok ? await pRes.json() : []);
      setStats(sRes.ok ? await sRes.json() : null);
      setSuggestions(gRes.ok ? (await gRes.json()).suggestions || [] : []);
    } finally { setLoading(false); }
  }, [headers]);

  const createFromSuggestion = async (basket) => {
    setActionError(null);
    const body = {
      supplier_id: basket.supplier_id,
      supplier_name: basket.supplier_name,
      lines: basket.lines.map(l => ({ ingredient_id: l.ingredient_id, description: l.description, qty: l.qty, unit_cost: l.unit_cost })),
    };
    try {
      const res = await fetch(`${API_URL}/api/procurement/purchase-orders`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) { load(); }
      else {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Could not create purchase order (${res.status}).`));
      }
    } catch {
      setActionError('Network error — could not reach the server.');
    }
  };

  useEffect(() => { load(); }, [load]);

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { description: '', qty: '', unit: '', unit_cost: '' }] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const setLine = (i, k, v) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));

  const createPO = async (e) => {
    e.preventDefault();
    setSaving(true); setFormError(null);
    try {
      const lines = form.lines
        .filter(l => l.description && parseFloat(l.qty) > 0)
        .map(l => clean({ description: l.description, qty: decStr(l.qty), unit: l.unit, unit_cost: decStr(l.unit_cost) || '0' }));
      if (lines.length === 0) { setFormError('Add at least one line with a description and quantity.'); setSaving(false); return; }
      const res = await fetch(`${API_URL}/api/procurement/purchase-orders`, { method: 'POST', headers, body: JSON.stringify(clean({ supplier_name: form.supplier_name, notes: form.notes, lines })) });
      if (res.ok) { setShowForm(false); setForm({ supplier_name: '', notes: '', lines: [{ description: '', qty: '', unit: '', unit_cost: '' }] }); load(); }
      else { const err = await res.json().catch(() => ({})); setFormError(typeof err.detail === 'string' ? err.detail : 'Could not create purchase order.'); }
    } catch { setFormError('Network error.'); } finally { setSaving(false); }
  };

  const sendPO = async (id) => {
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/procurement/purchase-orders/${id}/send`, { method: 'POST', headers });
      if (res.ok) { load(); if (detail?.id === id) openDetail(id); }
      else {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Could not send purchase order (${res.status}).`));
      }
    } catch {
      setActionError('Network error — could not reach the server.');
    }
  };
  const cancelPO = async (id) => {
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/procurement/purchase-orders/${id}`, { method: 'DELETE', headers });
      if (res.ok) { load(); setDetail(null); }
      else {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Could not cancel purchase order (${res.status}).`));
      }
    } catch {
      setActionError('Network error — could not reach the server.');
    }
  };
  const openDetail = async (id) => {
    const res = await fetch(`${API_URL}/api/procurement/purchase-orders/${id}`, { headers });
    if (res.ok) setDetail(await res.json());
  };

  const openReceive = (po) => { setReceiveError(null); setReceiveState({ po, lines: po.lines.map(l => ({ received_qty: l.qty, actual_unit_cost: l.unit_cost })) }); };
  const submitReceive = async () => {
    setReceiveError(null);
    const payload = { update_supplier_prices: true, lines: receiveState.lines.map((l, i) => ({ line_index: i, received_qty: decStr(l.received_qty) || '0', actual_unit_cost: decStr(l.actual_unit_cost) === '' ? null : decStr(l.actual_unit_cost) })) };
    try {
      const site = receiveState.po.location_id || localStorage.getItem('current_location_id');
      if (!site) { setReceiveError('Select a site before receiving this purchase order.'); return; }
      const res = await fetch(`${API_URL}/api/procurement/purchase-orders/${receiveState.po.id}/receive`, { method: 'POST', headers: { ...headers, 'X-Location-Id': site }, body: JSON.stringify(payload) });
      if (res.ok) { setReceiveState(null); load(); }
      else {
        const err = await res.json().catch(() => ({}));
        setReceiveError(apiErrorMessage(err.detail, `Could not confirm receipt (${res.status}).`));
      }
    } catch {
      setReceiveError('Network error — could not reach the server.');
    }
  };

  if (!orgId && !loading) return <div className="max-w-6xl mx-auto p-8 text-ink-muted" data-testid="proc-no-org">No active organization. Please log in.</div>;

  return (
    <div className="min-h-screen bg-surface-muted py-8" data-testid="procurement-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-accent-500 mb-1">Purchasing</div>
            <h1 className="font-display text-3xl text-ink">Procurement</h1>
          </div>
          <button onClick={() => { setFormError(null); setShowForm(true); }} data-testid="proc-add-po-btn" className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 motion-organic">
            <Plus className="w-4 h-4" /> New Purchase Order
          </button>
        </div>

        {actionError && (
          <div className="bg-danger/10 text-danger text-sm rounded-md px-4 py-3 mb-6 flex items-center justify-between" data-testid="proc-action-error">
            <span>{actionError}</span>
            <button onClick={() => setActionError(null)} className="ml-3 shrink-0"><X className="w-4 h-4" /></button>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Open POs" value={stats.open_pos} icon={ShoppingCart} />
            <StatCard label="Drafts" value={stats.draft_pos} icon={FileText} />
            <StatCard label="Outstanding" value={formatMoney(stats.outstanding_value)} icon={Send} />
            <StatCard label="Received" value={stats.received_pos} icon={PackageCheck} />
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="bg-accent-50 border border-accent-500/30 rounded-xl p-5 mb-6" data-testid="proc-suggestions">
            <div className="flex items-center gap-2 mb-3">
              <Plus className="w-4 h-4 text-accent-600" />
              <h2 className="font-display text-lg text-ink">Suggested Orders</h2>
              <span className="text-xs text-ink-muted">from low stock, at the cheapest vendor</span>
            </div>
            <div className="space-y-2">
              {suggestions.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-3 flex items-center justify-between border border-surface-border" data-testid={`proc-suggestion-${i}`}>
                  <div className="text-sm">
                    <span className="font-medium text-ink">{b.supplier_name}</span>
                    <span className="text-ink-muted"> · {b.lines.length} item(s) · est. {formatMoney(b.estimated_total)}</span>
                    <div className="text-xs text-ink-muted mt-0.5">{b.lines.map(l => `${l.description} ×${l.qty}`).join(', ')}</div>
                  </div>
                  <button onClick={() => createFromSuggestion(b)} data-testid={`proc-suggestion-create-${i}`} className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600 font-medium whitespace-nowrap">Create Draft PO</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-surface-border overflow-hidden">
          {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>
          : pos.length === 0 ? <div className="p-10 text-center text-ink-muted" data-testid="proc-empty">No purchase orders yet.</div>
          : (
            <table className="w-full text-sm" data-testid="proc-po-table">
              <thead className="bg-surface-sunken text-ink-muted text-xs uppercase">
                <tr><th className="text-left px-4 py-3">PO #</th><th className="text-left px-4 py-3">Supplier</th><th className="text-right px-4 py-3">Lines</th><th className="text-right px-4 py-3">Subtotal</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {pos.map(po => (
                  <tr key={po.id} className="border-t border-surface-border hover:bg-primary-50/40" data-testid={`proc-row-${po.id}`}>
                    <td className="px-4 py-3 font-medium text-ink">{po.po_number}</td>
                    <td className="px-4 py-3 text-ink-muted">{po.supplier_name}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{po.lines?.length || 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatMoney(po.subtotal)}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${statusBadge(po.status)}`}>{(po.status || '').replace('_', ' ')}</span></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openDetail(po.id)} data-testid={`proc-view-${po.id}`} className="text-primary-600 hover:underline text-xs font-medium mr-3">View</button>
                      {po.status === 'draft' && <button onClick={() => sendPO(po.id)} data-testid={`proc-send-${po.id}`} className="text-primary-600 hover:underline text-xs font-medium mr-3">Send</button>}
                      {(po.status === 'sent' || po.status === 'partially_received') && <button onClick={() => openReceive(po)} data-testid={`proc-receive-${po.id}`} className="text-ok hover:underline text-xs font-medium">Receive</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create PO modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="proc-form-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl text-ink">New Purchase Order</h2><button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-ink-muted" /></button></div>
            <form onSubmit={createPO} className="space-y-3">
              {formError && <div className="bg-danger/10 text-danger text-sm rounded-md px-3 py-2" data-testid="proc-form-error">{formError}</div>}
              <input required placeholder="Supplier name" value={form.supplier_name} onChange={e => setForm({ ...form, supplier_name: e.target.value })} data-testid="proc-form-supplier" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-ink-muted">Lines</div>
                {form.lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input placeholder="Item" value={l.description} onChange={e => setLine(i, 'description', e.target.value)} data-testid={`proc-line-desc-${i}`} className="col-span-5 px-2 py-2 border border-surface-border rounded-md text-sm" />
                    <input type="number" step="any" placeholder="Qty" value={l.qty} onChange={e => setLine(i, 'qty', e.target.value)} data-testid={`proc-line-qty-${i}`} className="col-span-2 px-2 py-2 border border-surface-border rounded-md text-sm" />
                    <input placeholder="Unit" value={l.unit} onChange={e => setLine(i, 'unit', e.target.value)} className="col-span-2 px-2 py-2 border border-surface-border rounded-md text-sm" />
                    <input type="number" step="any" placeholder="£/unit" value={l.unit_cost} onChange={e => setLine(i, 'unit_cost', e.target.value)} data-testid={`proc-line-cost-${i}`} className="col-span-2 px-2 py-2 border border-surface-border rounded-md text-sm" />
                    <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-ink-muted hover:text-danger flex justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={addLine} data-testid="proc-add-line" className="text-primary-600 text-sm hover:underline">+ Add line</button>
              </div>
              <textarea placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <button type="submit" disabled={saving} data-testid="proc-form-submit" className="w-full bg-primary-500 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50">{saving ? 'Saving…' : 'Create Draft PO'}</button>
            </form>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-end z-50" data-testid="proc-detail-drawer" onClick={() => setDetail(null)}>
          <div className="bg-white h-full w-full max-w-lg p-6 overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2"><h2 className="font-display text-xl text-ink">{detail.po_number}</h2><button onClick={() => setDetail(null)}><X className="w-5 h-5 text-ink-muted" /></button></div>
            <div className="text-sm text-ink-muted mb-4">{detail.supplier_name} · <span className={`text-xs px-2 py-0.5 rounded ${statusBadge(detail.status)}`}>{(detail.status || '').replace('_', ' ')}</span></div>
            <table className="w-full text-sm mb-4">
              <thead className="text-ink-muted text-xs uppercase"><tr><th className="text-left py-1">Item</th><th className="text-right py-1">Qty</th><th className="text-right py-1">Recv</th><th className="text-right py-1">£/unit</th><th className="text-right py-1">Total</th></tr></thead>
              <tbody>{detail.lines.map((l, i) => <tr key={i} className="border-t border-surface-border"><td className="py-1.5">{l.description}</td><td className="text-right tabular-nums">{l.qty}</td><td className="text-right tabular-nums">{l.received_qty || 0}</td><td className="text-right tabular-nums">£{fmt(l.unit_cost)}</td><td className="text-right tabular-nums">£{fmt(l.line_total)}</td></tr>)}</tbody>
            </table>
            <div className="text-right font-medium text-ink mb-4">Subtotal {formatMoney(detail.subtotal)}</div>
            <div className="flex gap-2">
              {detail.status === 'draft' && <button onClick={() => sendPO(detail.id)} className="flex-1 bg-primary-500 text-white py-2 rounded-md text-sm">Send to Supplier</button>}
              {(detail.status === 'sent' || detail.status === 'partially_received') && <button onClick={() => { setDetail(null); openReceive(detail); }} className="flex-1 bg-ok text-white py-2 rounded-md text-sm">Receive Goods</button>}
              {detail.status !== 'received' && detail.status !== 'partially_received' && detail.status !== 'cancelled' && <button onClick={() => cancelPO(detail.id)} className="px-4 py-2 border border-danger text-danger rounded-md text-sm">Cancel</button>}
            </div>
          </div>
        </div>
      )}

      {/* Receive modal */}
      {receiveState && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="proc-receive-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl text-ink">Receive — {receiveState.po.po_number}</h2><button onClick={() => setReceiveState(null)}><X className="w-5 h-5 text-ink-muted" /></button></div>
            {receiveError && <div className="bg-danger/10 text-danger text-sm rounded-md px-3 py-2 mb-3" data-testid="proc-receive-error">{receiveError}</div>}
            <div className="space-y-2">
              {receiveState.po.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                  <div className="col-span-5">{l.description}<div className="text-xs text-ink-muted">ordered {l.qty}</div></div>
                  <input type="number" step="any" value={receiveState.lines[i].received_qty} onChange={e => setReceiveState(s => ({ ...s, lines: s.lines.map((x, idx) => idx === i ? { ...x, received_qty: e.target.value } : x) }))} data-testid={`proc-recv-qty-${i}`} className="col-span-3 px-2 py-2 border border-surface-border rounded-md" placeholder="received" />
                  <input type="number" step="any" value={receiveState.lines[i].actual_unit_cost} onChange={e => setReceiveState(s => ({ ...s, lines: s.lines.map((x, idx) => idx === i ? { ...x, actual_unit_cost: e.target.value } : x) }))} className="col-span-4 px-2 py-2 border border-surface-border rounded-md" placeholder="actual £/unit" />
                </div>
              ))}
            </div>
            <button onClick={submitReceive} data-testid="proc-receive-submit" className="w-full mt-4 bg-ok text-white py-2.5 rounded-md text-sm font-medium">Confirm Receipt</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Procurement;
